import React from 'react';
import { View, Text, StyleSheet } from '@react-pdf/renderer';

const styles = StyleSheet.create({
    container: {
        padding: 10,
    },
    sectionTitle: {
        fontSize: 14,
        fontWeight: 'bold',
        marginBottom: 15,
        color: '#1e40af',
        borderBottomWidth: 1,
        borderBottomColor: '#e0e0e0',
        paddingBottom: 5,
    },
    contentContainer: {
        flexDirection: 'row',
        gap: 20,
    },
    leftColumn: {
        flex: 1,
        paddingRight: 10,
    },
    rightColumn: {
        flex: 1,
        paddingLeft: 10,
    },
    subsectionTitle: {
        fontSize: 12,
        fontWeight: 'bold',
        marginTop: 10,
        marginBottom: 8,
        color: '#374151',
        backgroundColor: '#f3f4f6',
        padding: 6,
    },
    table: {
        display: 'flex',
        width: '100%',
        marginBottom: 10,
        borderWidth: 1,
        borderColor: '#e5e7eb',
    },
    tableRow: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderBottomColor: '#e5e7eb',
        paddingVertical: 5,
    },
    tableHeader: {
        backgroundColor: '#f9fafb',
        fontWeight: 'bold',
    },
    labelCell: {
        width: '50%',
        paddingHorizontal: 8,
        fontSize: 9,
        fontWeight: 'bold',
        color: '#374151',
    },
    valueCell: {
        width: '50%',
        paddingHorizontal: 8,
        fontSize: 9,
        color: '#111827',
    },
    transportSection: {
        marginTop: 10,
    },
    transportItem: {
        marginBottom: 8,
        padding: 8,
        backgroundColor: '#f8fafc',
        borderRadius: 3,
        borderWidth: 1,
        borderColor: '#e2e8f0',
    },
    transportTitle: {
        fontSize: 10,
        fontWeight: 'bold',
        marginBottom: 4,
        color: '#1e40af',
    },
    transportDetail: {
        fontSize: 9,
        color: '#374151',
        marginBottom: 2,
    },
});

interface PalletDistributionPDFProps {
    data: {
        project_name?: string;
        pallet_pattern?: string;
        number_of_layers?: string;
        cases_per_layer?: string;
        unit_load_length?: string;
        unit_load_length_unit?: string;
        unit_load_width?: string;
        unit_load_width_unit?: string;
        unit_load_height?: string;
        unit_load_height_unit?: string;
        unitload_weight?: string;
        unitload_weight_unit?: string;
        ftl_top?: string;
        ftl_middle?: string;
        ftl_bottom?: string;
        wraps_top?: string;
        wraps_middle?: string;
        wraps_bottom?: string;
        film_gauge?: string;
        transport?: Array<{
            transport_mode?: string;
            max_distance?: string;
            max_distance_unit?: string;
        }>;
        stacking_pattern?: string;
        stack_duration?: string;
    };
}

export const PalletDistributionPDF: React.FC<PalletDistributionPDFProps> = ({ data }) => {
    const formatValue = (value: string | undefined, unit?: string) => {
        if (!value) return 'N/A';
        return unit ? `${value} ${unit}` : value;
    };

    return (
        <View style={styles.container}>
            <Text style={styles.sectionTitle}>Pallet Configuration & Distribution Settings</Text>

            <View style={styles.contentContainer}>
                {/* Left Column */}
                <View style={styles.leftColumn}>
                    {/* Pallet Pattern Design (now includes Unit Load Dimensions) */}
                    <Text style={styles.subsectionTitle}>Pallet Pattern Design</Text>
                    <View style={styles.table}>
                        {/* <View style={[styles.tableRow, styles.tableHeader]}>
                            <Text style={styles.labelCell}>Parameter</Text>
                            <Text style={styles.valueCell}>Value</Text>
                        </View> */}
                        <View style={styles.tableRow}>
                            <Text style={styles.labelCell}>Pallet Pattern Design:</Text>
                            <Text style={styles.valueCell}>{formatValue(data.pallet_pattern)}</Text>
                        </View>
                        <View style={styles.tableRow}>
                            <Text style={styles.labelCell}>Number of Layers:</Text>
                            <Text style={styles.valueCell}>{formatValue(data.number_of_layers)}</Text>
                        </View>
                        <View style={styles.tableRow}>
                            <Text style={styles.labelCell}>Cases per Layer:</Text>
                            <Text style={styles.valueCell}>{formatValue(data.cases_per_layer)}</Text>
                        </View>
                        <View style={styles.tableRow}>
                            <Text style={styles.labelCell}>Unit Load Length:</Text>
                            <Text style={styles.valueCell}>{formatValue(data.unit_load_length, data.unit_load_length_unit)}</Text>
                        </View>
                        <View style={styles.tableRow}>
                            <Text style={styles.labelCell}>Unit Load Width:</Text>
                            <Text style={styles.valueCell}>{formatValue(data.unit_load_width, data.unit_load_width_unit)}</Text>
                        </View>
                        <View style={styles.tableRow}>
                            <Text style={styles.labelCell}>Unit Load Height:</Text>
                            <Text style={styles.valueCell}>{formatValue(data.unit_load_height, data.unit_load_height_unit)}</Text>
                        </View>
                        <View style={styles.tableRow}>
                            <Text style={styles.labelCell}>Unit Load Weight:</Text>
                            <Text style={styles.valueCell}>{formatValue(data.unitload_weight, data.unitload_weight_unit)}</Text>
                        </View>
                    </View>

                    {/* Palletizing Recipe (now includes Stretch Wrap Configuration and Stacking Configuration) */}
                    <Text style={styles.subsectionTitle}>Palletizing Recipe</Text>
                    <View style={styles.table}>
                        {/* <View style={[styles.tableRow, styles.tableHeader]}>
                            <Text style={styles.labelCell}>Parameter</Text>
                            <Text style={styles.valueCell}>Value</Text>
                        </View> */}
                        <View style={styles.tableRow}>
                            <Text style={styles.labelCell}>Film Gauge:</Text>
                            <Text style={styles.valueCell}>{formatValue(data.film_gauge)}</Text>
                        </View>
                        <View style={styles.tableRow}>
                            <Text style={styles.labelCell}>FTL Top:</Text>
                            <Text style={styles.valueCell}>{formatValue(data.ftl_top)}</Text>
                        </View>
                        <View style={styles.tableRow}>
                            <Text style={styles.labelCell}>FTL Middle:</Text>
                            <Text style={styles.valueCell}>{formatValue(data.ftl_middle)}</Text>
                        </View>
                        <View style={styles.tableRow}>
                            <Text style={styles.labelCell}>FTL Bottom:</Text>
                            <Text style={styles.valueCell}>{formatValue(data.ftl_bottom)}</Text>
                        </View>
                        <View style={styles.tableRow}>
                            <Text style={styles.labelCell}>Wraps Top:</Text>
                            <Text style={styles.valueCell}>{formatValue(data.wraps_top)}</Text>
                        </View>
                        <View style={styles.tableRow}>
                            <Text style={styles.labelCell}>Wraps Middle:</Text>
                            <Text style={styles.valueCell}>{formatValue(data.wraps_middle)}</Text>
                        </View>
                        <View style={styles.tableRow}>
                            <Text style={styles.labelCell}>Wraps Bottom:</Text>
                            <Text style={styles.valueCell}>{formatValue(data.wraps_bottom)}</Text>
                        </View>
                        <View style={styles.tableRow}>
                            <Text style={styles.labelCell}>Stacking Pattern:</Text>
                            <Text style={styles.valueCell}>{formatValue(data.stacking_pattern)}</Text>
                        </View>
                        <View style={styles.tableRow}>
                            <Text style={styles.labelCell}>Stack Duration:</Text>
                            <Text style={styles.valueCell}>{formatValue(data.stack_duration)}</Text>
                        </View>
                    </View>
                </View>

                {/* Right Column */}
                <View style={styles.rightColumn}>
                    {/* Transport Modes */}
                    <Text style={styles.subsectionTitle}>Transport Configuration</Text>
                    <View style={styles.transportSection}>
                        {data.transport && data.transport.length > 0 ? (
                            data.transport.map((transport, index) => (
                                <View key={index} style={styles.transportItem}>
                                    <Text style={styles.transportTitle}>
                                        Transport Mode {index + 1}
                                    </Text>
                                    <Text style={styles.transportDetail}>
                                        Mode: {formatValue(transport.transport_mode)}
                                    </Text>
                                    <Text style={styles.transportDetail}>
                                        Max Distance: {formatValue(transport.max_distance, transport.max_distance_unit)}
                                    </Text>
                                </View>
                            ))
                        ) : (
                            <Text style={{ fontSize: 9, color: '#6b7280', fontStyle: 'italic' }}>
                                No transport modes configured
                            </Text>
                        )}
                    </View>

                    {/* Configuration Notes */}
                    {/* <View style={{ marginTop: 15, padding: 8, backgroundColor: '#fef3c7', borderRadius: 3 }}>
                        <Text style={{ fontSize: 10, fontWeight: 'bold', marginBottom: 5, color: '#92400e' }}>
                            Configuration Notes:
                        </Text>
                        <Text style={{ fontSize: 8, color: '#92400e', marginBottom: 2 }}>
                            • Values marked as "Auto-calculated" are computed based on input parameters
                        </Text>
                        <Text style={{ fontSize: 8, color: '#92400e', marginBottom: 2 }}>
                            • FTL (Force to Load) values are calculated based on unit load weight
                        </Text>
                        <Text style={{ fontSize: 8, color: '#92400e' }}>
                            • Transport and stacking configurations affect risk analysis results
                        </Text>
                    </View> */}
                </View>
            </View>
        </View>
    );
}; 